// ============================================================
// DM-LMS — Shared Utilities
// DRY: pure functions used by API and Web.
// ============================================================

import type { Role, PaymentStatus, Course, TransactionStatus, ConfigCategory } from "../types/index.js";
import { ADMIN_ONLY_CONFIG_CATEGORIES } from "../types/index.js";
import type { ApiSuccess, ApiError, PaginatedResult } from "../types/index.js";
import {
  CODE_PREFIXES,
  KENYA_COUNTRY_CODE,
  COUNTRIES,
  CERT_MIN_ATTENDANCE_PERCENT,
  ACCOUNT_REF_PLACEHOLDERS,
  DEFAULT_ACCOUNT_REF_FORMAT,
  TUTOR_EMAIL_KEY_LENGTH,
} from "../constants/index.js";

// ---- Entity code generation ----
// Generates ST-0001, TU-0001, CR-0001 etc.
export function generateCode(role: Role | "course" | "payment" | "certificate", sequence: number): string {
  const prefix = CODE_PREFIXES[role];
  return `${prefix}-${String(sequence).padStart(4, "0")}`;
}

// On/off flags stored as a system_config label ("true" / "false"). Tolerant of case and spaces so an
// admin typing "True" does not silently switch a feature off; anything else, or a missing row, is off.
export function isConfigFlagEnabled(label: string | undefined): boolean {
  return label?.trim().toLowerCase() === "true";
}

// ---- M-Pesa account reference ----
// The course's prefix for payment references: the admin-set accountRef, else the course code.
export function resolveCourseAccountRef(course: Pick<Course, "accountRef" | "code">): string {
  return course.accountRef ?? course.code;
}

// Builds what the student types as the M-Pesa account number, e.g. "MF-PAY-0001".
// A format without the payment placeholder would not be unique per payment, so the default is used instead.
// With no course prefix (a payment that predates courses being linked) the payment code stands alone.
export function formatAccountReference(format: string, courseRef: string | null, paymentCode: string): string {
  if (courseRef === null) return paymentCode;
  const template = format.includes(ACCOUNT_REF_PLACEHOLDERS.payment) ? format : DEFAULT_ACCOUNT_REF_FORMAT;
  return template
    .split(ACCOUNT_REF_PLACEHOLDERS.course).join(courseRef)
    .split(ACCOUNT_REF_PLACEHOLDERS.payment).join(paymentCode);
}

// The single comparison form for M-Pesa transaction codes. Every comparison and every stored
// reference goes through this — never compare or uppercase codes inline.
export function normaliseMpesaCode(code: string): string {
  return code.trim().toUpperCase();
}

// ---- Money ----
// The one KES formatter, shared by the web app and by server messages. Fixed to en-US so the API and every
// browser print the same thousands separators ("18,000"), whatever the viewer's locale.
export function formatAmount(amountKes: number): string {
  return amountKes.toLocaleString("en-US");
}

// "KES 18,000"
export function formatKes(amountKes: number): string {
  return `KES ${formatAmount(amountKes)}`;
}

// Why a manual confirmation was refused: "Mark paid" always means the full amount, never a part.
export function paymentAmountMismatchMessage(paymentAmountKes: number): string {
  return `Amount received must equal the payment amount (${formatKes(paymentAmountKes)}).`;
}

// Reference for ANY proof type: all whitespace removed and uppercased, so a pasted "qgh7 x8y9z0" and
// "QGH7X8Y9Z0" are the same reference. Used by the form as the admin types, by the schema and by the
// duplicate check — one definition, so what is shown, validated and compared never drift. For a valid
// M-Pesa code (letters and digits only) it equals normaliseMpesaCode.
export function normaliseProofReference(reference: string): string {
  return reference.replace(/\s+/g, "").toUpperCase();
}

// Payments that went from pending/failed to success between two fetches of the same list.
// A payment absent from `previous` (first load, or a brand-new row) never counts — only a real transition does.
export function findNewlyVerifiedPayments<T extends { id: string; status: TransactionStatus }>(
  previous: readonly T[],
  next: readonly T[]
): T[] {
  const before = new Map(previous.map((payment) => [payment.id, payment.status]));
  return next.filter((payment) => {
    const was = before.get(payment.id);
    return payment.status === "success" && (was === "pending" || was === "failed");
  });
}

// ---- Config: admin-only categories + tutor email keys ----
export function isAdminOnlyCategory(category: string): category is (typeof ADMIN_ONLY_CONFIG_CATEGORIES)[number] {
  return (ADMIN_ONLY_CONFIG_CATEGORIES as readonly ConfigCategory[]).includes(category as ConfigCategory);
}

export function normaliseEmail(email: string): string {
  return email.trim().toLowerCase();
}

// Row key for a tutor_email entry: emails can't be keys (CONFIG_KEY_PATTERN), so the key is the
// first TUTOR_EMAIL_KEY_LENGTH hex chars of SHA-256(normalised email). Deterministic, so the
// unique (category, key) index rejects duplicates.
export async function deriveTutorEmailKey(email: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(normaliseEmail(email)));
  const hex = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
  return hex.slice(0, TUTOR_EMAIL_KEY_LENGTH);
}

// ---- API response builders ----
export function success<T>(data: T): ApiSuccess<T> {
  return { ok: true, data };
}

export function failure(error: string, code: string, statusCode: number): ApiError {
  return { ok: false, error, code, statusCode };
}

// ---- Pagination helper ----
export function getOffset(page: number, pageSize: number): number {
  return (page - 1) * pageSize;
}

export function paginate<T>(items: T[], total: number, page: number, pageSize: number): PaginatedResult<T> {
  return {
    items,
    total,
    page,
    pageSize,
    hasMore: page * pageSize < total,
  };
}

// ---- Date helpers ----
export function toIso(date: Date): string {
  return date.toISOString();
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

// ---- Pseudonymise PII for external calls ----
// Pass codes to any external API — never names or emails.
export function pseudonymise(code: string): string {
  return code; // The code IS the pseudonym — names never leave the DB
}

// ---- Attendance percentage ----
export function calcAttendancePercent(attended: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((attended / total) * 100);
}

// ---- Certificate eligibility ----
// Requires minimum attendance AND payment status "paid".
export function checkEligibility(attendancePercent: number, paymentStatus: PaymentStatus): boolean {
  return attendancePercent >= CERT_MIN_ATTENDANCE_PERCENT && paymentStatus === "paid";
}

// ---- Student profile completeness ----

export interface StudentProfileFields {
  phone:                  string | null;
  idNumber:               string | null;
  organization:           string | null;
  profession:             string | null;
  country:                string | null;
  county:                 string | null;
  region:                 string | null;
  preferredPaymentMethod: string | null;
}

const hasText = (value: string | null | undefined): boolean => (value ?? "").trim() !== "";

export interface StudentProfileCompletion {
  done:    number;
  total:   number;
  percent: number;
}

// The ONE definition of what a student profile needs: phone, ID number, payment preference,
// organisation, profession and country, plus a county when the country is Kenya, otherwise a region.
// Always 7 checks. Blank or whitespace-only text does not count.
export function studentProfileCompletion(fields: StudentProfileFields): StudentProfileCompletion {
  const placeField = fields.country === KENYA_COUNTRY_CODE ? fields.county : fields.region;
  const checks = [
    fields.phone,
    fields.idNumber,
    fields.preferredPaymentMethod,
    fields.organization,
    fields.profession,
    fields.country,
    placeField,
  ].map(hasText);
  const done = checks.filter(Boolean).length;
  return { done, total: checks.length, percent: Math.round((done / checks.length) * 100) };
}

// A complete student profile has every required field; the route stores the result as profile_complete.
export function isStudentProfileComplete(fields: StudentProfileFields): boolean {
  const { done, total } = studentProfileCompletion(fields);
  return done === total;
}

// English name for a stored ISO 3166-1 alpha-2 code; "—" when unset, the raw code when unknown.
export function getCountryName(code: string | null): string {
  if (!code) return "—";
  return COUNTRIES.find((country) => country.code === code)?.name ?? code;
}
